import { ymdToLocalDate } from '../../utils/flightDateUtils';
import { formatCalendarPriceModule } from './flightForm.utils';
import {
  FARE_DATE_WEEK_LENGTH,
  FARE_MONTH_NAMES,
  FARE_PRICE_COLOR_HIGH,
  FARE_PRICE_COLOR_LOW,
  FARE_PRICE_COLOR_MID,
} from './fareDateGrid.constants';

/**
 * Local calendar day as YYYY-MM-DD. Empty when the value is missing.
 * @param {Date|string|null|undefined} value
 * @returns {string}
 */
export function toIsoDate(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
  }
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * @param {Record<string, number>|null|undefined} pricesMap
 * @returns {{ p25: number, p75: number }|null}
 */
export function buildFarePriceThresholds(pricesMap) {
  if (!pricesMap) return null;
  const prices = Object.values(pricesMap).filter((price) => typeof price === 'number' && price > 0);
  if (prices.length < 3) return null;
  const sorted = prices.slice().sort((a, b) => a - b);
  return {
    p25: sorted[Math.floor(sorted.length * 0.25)],
    p75: sorted[Math.floor(sorted.length * 0.75)],
  };
}

/**
 * @param {number} price
 * @param {{ p25: number, p75: number }|null} thresholds
 * @returns {string}
 */
export function farePriceColor(price, thresholds) {
  if (!thresholds || typeof price !== 'number') return FARE_PRICE_COLOR_LOW;
  if (price <= thresholds.p25) return FARE_PRICE_COLOR_LOW;
  if (price <= thresholds.p75) return FARE_PRICE_COLOR_MID;
  return FARE_PRICE_COLOR_HIGH;
}

function priceFields(pricesMap, thresholds, iso) {
  const price = pricesMap?.[iso];
  if (typeof price !== 'number') return { label: '', color: '' };
  return {
    label: formatCalendarPriceModule(price),
    color: farePriceColor(price, thresholds),
  };
}

/**
 * Month grids for the fare calendar. Prices are formatted here so a tap does not redo that work.
 * @param {{ monthCount: number, minKey: string, maxKey?: string, departurePrices?: object, returnPrices?: object }} input
 */
export function buildFareDateGrid({ monthCount, minKey, maxKey, departurePrices, returnPrices }) {
  if (!monthCount || monthCount < 1 || !minKey) return null;
  const min = ymdToLocalDate(minKey);
  if (!min) return null;
  const departureThresholds = buildFarePriceThresholds(departurePrices);
  const returnThresholds = buildFarePriceThresholds(returnPrices);
  const months = [];

  for (let offset = 0; offset < monthCount; offset += 1) {
    const first = new Date(min.getFullYear(), min.getMonth() + offset, 1);
    const year = first.getFullYear();
    const monthIndex = first.getMonth();
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const cells = [];
    for (let pad = 0; pad < first.getDay(); pad += 1) cells.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) {
      const iso = toIsoDate(new Date(year, monthIndex, day));
      const departure = priceFields(departurePrices, departureThresholds, iso);
      const returning = priceFields(returnPrices, returnThresholds, iso);
      cells.push({
        iso,
        day,
        disabled: iso < minKey || Boolean(maxKey && iso > maxKey),
        departureLabel: departure.label,
        departureColor: departure.color,
        returnLabel: returning.label,
        returnColor: returning.color,
      });
    }
    while (cells.length % FARE_DATE_WEEK_LENGTH !== 0) cells.push(null);
    months.push({
      id: `${year}-${String(monthIndex + 1).padStart(2, '0')}`,
      title: `${FARE_MONTH_NAMES[monthIndex]}, ${year}`,
      cells,
    });
  }

  return { months };
}

/**
 * One-way replaces the start. Round-trip starts a new range, then sets the return.
 * @param {{ start: string|null, end: string|null }} selection
 * @param {string} iso
 * @param {string} tripType
 */
export function applyDatePick(selection, iso, tripType) {
  if (tripType !== 'roundtrip') return { start: iso, end: null };
  const start = selection?.start || null;
  const end = selection?.end || null;
  if (!start || end || iso < start) return { start: iso, end: null };
  return { start, end: iso };
}

/**
 * @param {string} iso
 * @param {{ start: string|null, end: string|null }} selection
 * @param {string} tripType
 * @returns {'plain'|'selected'|'range-start'|'range-end'|'range-single'|'in-range'}
 */
export function selectionRole(iso, selection, tripType) {
  const start = selection?.start || null;
  const end = tripType === 'roundtrip' ? (selection?.end || null) : null;
  if (!start) return 'plain';
  if (!end) return iso === start ? 'selected' : 'plain';
  if (iso === start && iso === end) return 'range-single';
  if (iso === start) return 'range-start';
  if (iso === end) return 'range-end';
  if (iso > start && iso < end) return 'in-range';
  return 'plain';
}

/**
 * Return fares show on the departure day and after it.
 * @param {string} iso
 * @param {string|null} start
 * @param {string} tripType
 */
export function usesReturnFare(iso, start, tripType) {
  return tripType === 'roundtrip' && Boolean(start) && iso >= start;
}

/**
 * Write the picked dates onto a ref the Save button can read immediately.
 * @param {{ current: { start: Date|null, end: Date|null } }|null|undefined} publishedRef
 * @param {{ start: string|null, end: string|null }} selection
 */
export function publishFareSelection(publishedRef, selection) {
  if (!publishedRef) return;
  publishedRef.current = {
    start: ymdToLocalDate(selection?.start) || null,
    end: ymdToLocalDate(selection?.end) || null,
  };
}

/**
 * Let the highlight paint, then run the parent update.
 * A timer is used because some in-app browsers delay animation frames.
 * Returns a cancel function so a clear can drop a stale date.
 * @param {() => void} work
 * @returns {() => void}
 */
export function deferAfterPaint(work) {
  let cancelled = false;
  const timeoutId = window.setTimeout(() => {
    if (!cancelled) work();
  }, 0);
  return () => {
    cancelled = true;
    window.clearTimeout(timeoutId);
  };
}

/**
 * Unmount the sheet first. Apply search state on the next turn.
 * @param {() => void} close
 * @param {() => void} work
 */
export function closeThenRun(close, work) {
  if (typeof close === 'function') close();
  window.setTimeout(() => {
    if (typeof work === 'function') work();
  }, 0);
}
